import React, { useCallback, useEffect } from "react"
import { Tooltip } from "react-tooltip"
import { ISharedContext, IActivatable } from "../support/registration_form_component"
import { FormHelpers } from "../support/form_helpers"
import { isBlank } from "../support/helpers"
import { ContactFillingForm } from "../support/contact_filling_form"
import { ClaimValueForm } from "../support/claim_value_form"
import * as data from "../data"
import * as config from "../clients/config"
import { LOOK_UP_CONTACT_ZIP_CODE } from "../wizard"
import { LicenseTypeMultiSelect } from "../support/license_type_multi_select"
import { MarketCategoriesMultiSelect } from "../support/market_categories_multi_select"
import { TpaSelfInsuredForm } from "../support/tpa_self_insured_form"
import { NaicFormGroups } from "../support/naic_form_groups"
import { FeinFormGroups } from "../support/fein_form_groups"
import { InsurerTypeLabels } from "../clients/config"

export type IRegistrationGeneralInfoProps = IActivatable & ISharedContext & {
  dental: boolean
  showEstimates?: boolean
  submitterCodeTooltip?: string
  /**
   * OHCA-specific. Appears above "Submitter Name", if enabled in client config.
   */
  parentCompanyNameOptions?: string[]
  /**
   * HCAI-specific. Appears below "Submitter Name", and ties in to Plan Registration.
   */
  submitterParentNameOptions?: string[]
}
export function RegistrationGeneralInfo(props: IRegistrationGeneralInfoProps): React.ReactNode {
  if (!props.active) {
    return <div />
  }

  const fillerConfig = props.config.fillerConfig

  return (
    <div className="step">
      <h2 className="visible-print">
          Step 1: Organization Information
      </h2>

      <div className="panel panel-info rereg" style={{ display: "none" }}>
        <div className="panel-heading">
          <h1 className="panel-title">Please note!</h1>
        </div>
        <div className="panel-body">
            We have pre-filled some information based on your registration from 2015. If anything has changed please
            correct the information below as you review the form.
        </div>
      </div>

      <div className="page-break-after">
        <ContactFillingForm
          {...props}
          key="filler"
          name="filler"
          title={fillerConfig.title}
          description={fillerConfig.description}
          required={fillerConfig.required}
          showNotApplicable={false}
          email_confirmation={true}
          default_to_new_contact={true}
          showPlanSelection={false}
        />
      </div>

      <Insurer {...props} />

      {props.showEstimates === true && (
        FormHelpers.coverageTypes().map((coverageType) => {
          if (coverageType.field === "dental" && props.dental === false) {
            return null
          }

          return (
            <div className={`coverage ${coverageType.field} page-break-after`} key={coverageType.field}>
              <ClaimValueForm coverageType={coverageType} {...props} />
            </div>
          )
        })
      )}
    </div>
  )
}


export interface IInsurerProps extends ISharedContext {
  submitterCodeTooltip?: string
  /**
   * OHCA-specific. Appears above "Submitter Name", if enabled in client config.
   */
  parentCompanyNameOptions?: string[]
  /**
   * HCAI-specific. Appears below "Submitter Name", and ties in to Plan Registration.
   */
  submitterParentNameOptions?: string[]
}
function Insurer(props: IInsurerProps): React.ReactNode {
  const {
    form,
    dispatch,
    setFormValue,
    onInputChange,
  } = props

  useEffect(() => {
    if (props.config.hasParentCompanyName && !props.parentCompanyNameOptions?.length) {
      console.warn("Client config property 'hasParentCompanyName' is 'true', but no 'parentCompanyNameOptions' have been provided!")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: only run once
  }, []);

  const onNaicChange = useCallback((naics: string[]) => {
    dispatch({
      action: "UPDATE_FORM",
      form: {
        insurer: {
          ...form?.insurer,
          naics: naics,
        },
      },
    })
  }, [form?.insurer, dispatch])

  const onFeinChange = useCallback((feins: string[]) => {
    dispatch({
      action: "UPDATE_FORM",
      form: {
        insurer: {
          ...form?.insurer,
          feins: feins,
        },
      },
    })
  }, [form?.insurer, dispatch])

  const addLicenseType = useCallback(() => {
    setFormValue(["insurer", "license_types"], [...(form?.insurer?.license_types || []), {}])
  }, [form?.insurer, setFormValue])

  const removeLicenseType = useCallback((index: number) => {
    dispatch({
      action: "REMOVE_LICENSE_TYPE",
      index: index,
    })
  }, [dispatch])

  const onLicenseTypeChange = useCallback((index: number, key: string, value: string) => {
    setFormValue(["insurer", "license_types", index.toString(), key], value)
  }, [setFormValue])

  const addMarketCategory = useCallback(() => {
    setFormValue(["insurer", "market_categories"], [...(form?.insurer?.market_categories || []), ""])
  }, [setFormValue, form?.insurer?.market_categories])

  const removeMarketCategory = useCallback(() => {
    const numCategories = (form?.insurer?.market_categories?.length || 0)
    if (numCategories <= 1) {
      return
    }

    // remove the last category
    dispatch({
      action: "REMOVE_MARKET_CATEGORY",
      index: numCategories - 1,
    })
  }, [form?.insurer?.market_categories?.length, dispatch])

  const setManuallyEditedCompany = useCallback(() => {
    setFormValue(["insurer", "manually_edited_company"], true)
  }, [setFormValue])

  const onCompanyNameChange = useCallback((event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setManuallyEditedCompany()
    onInputChange(event)
  }, [setManuallyEditedCompany, onInputChange])

  const onInsurerZipCodeBlur = useCallback((event: React.FocusEvent<HTMLInputElement>) => {
    dispatch({
      action: LOOK_UP_CONTACT_ZIP_CODE,
      path: ["insurer"],
    })
  }, [dispatch])

  let insurer: data.IInsurer = form?.insurer || {}
  if (typeof insurer === "string") {
    insurer = {}
  }

  const mainInputClass = "col-sm-9"
  const labelClass = "col-sm-3 control-label"
  const labelClassRequired = "col-sm-3 control-label required"

  const submitterCodeTooltip = props.submitterCodeTooltip || "If known, please enter your Onpoint-assigned submitter code."

  let shouldDisplayFullAddress = true
  const { city, zip, state, country_code } = insurer

  if (
    isBlank(city) &&
      isBlank(state) &&
      isBlank(country_code) &&
      (isBlank(zip) || (zip && zip.length && zip.length < 5))
  ) {
    shouldDisplayFullAddress = false
  }

  let naics = insurer?.naics
  if (!Array.isArray(naics) || naics.length === 0) {
    naics = [""]
  }

  let feins = insurer?.feins
  if (!Array.isArray(feins) || feins.length === 0) {
    feins = [""]
  }

  let feinRequired = props.config.feinRequired

  if (insurer.type === "Government Provider") {
    feinRequired = false
  }

  const insurerZipNotFound = insurer.zip_found === false

  return (
    <div className="panel panel-default company-information page-break-after">
      <div className="panel-heading">
        <h1 className="panel-title">Organization Information</h1>
        <p className="panel-description">Please provide the following information regarding the type of organization that you represent.</p>
      </div>
      <div className="panel-body form-horizontal">
        {props.config.hasParentCompanyName && (<>
          <div className="form-group">
            <label className={labelClassRequired} htmlFor="insurer_parent_company_name">
              Parent Company Name
            </label>
            <div className={mainInputClass}>
              <select
                name="insurer[parent_company_name]"
                id="insurer_parent_company_name"
                value={insurer.parent_company_name ?? ""}
                onChange={onInputChange}
                className="form-control"
                required={true}
              >
                <option/>
                {insurer.parent_company_name && insurer.parent_company_name !== data.OTHER && !props.parentCompanyNameOptions?.includes(insurer.parent_company_name) && (
                  <option value={insurer.parent_company_name}>{insurer.parent_company_name}</option>
                )}
                {props.parentCompanyNameOptions?.map((name) => {
                  return (
                    <option value={name} key={name}>
                      {name}
                    </option>
                  )
                })}
                <option value={data.OTHER}>I don&apos;t see my parent company</option>
              </select>
            </div>
          </div>
          {insurer.parent_company_name === data.OTHER && (
            <div className="form-group">
              <div className="col-sm-9 col-sm-offset-3">
                <input
                  name="insurer[custom_parent_company_name]"
                  id="insurer_custom_parent_company_name"
                  value={insurer.custom_parent_company_name ?? ""}
                  onChange={onInputChange}
                  className="form-control"
                  required={true}
                  placeholder="Parent Company Name"
                  data-fv-notempty-message="Please enter your parent company name."
                />
              </div>
            </div>
          )}
        </>)}
        <div className="form-group">
          <label className={labelClassRequired} htmlFor="insurer_company_name">
            Submitter Name &nbsp;
            <i className="fa fa-question-circle"
              data-tooltip-id="tip-submitter-name"
              data-tooltip-content="Please enter the Legal Entity Name."
            />
            <Tooltip id="tip-submitter-name" className="registration-tooltip"/>
          </label>
          <div className={mainInputClass}>
            <input
              name="insurer[company_name]"
              id="insurer_company_name"
              value={insurer.company_name ?? ""}
              onChange={onCompanyNameChange}
              className="form-control"
              placeholder="Submitter Name"
              required={true}
              maxLength={120}
            />
          </div>
        </div>
        {props.submitterParentNameOptions && (
          <div className="form-group">
            <label className={labelClassRequired} htmlFor="insurer_payer_name">
                Submitter Parent Name
            </label>
            <div className={mainInputClass}>
              <select
                id="insurer_payer_name"
                name="insurer[payer_name]"
                value={insurer.payer_name ?? ""}
                onChange={onCompanyNameChange}
                className="form-control"
                required={true}
              >
                <option/>
                {props.submitterParentNameOptions.map((name) => {
                  return (
                    <option value={name} key={name}>
                      {name}
                    </option>
                  )
                })}
              </select>
            </div>
          </div>
        )}
        <div className="form-group">
          <label className={labelClass} htmlFor="insurer_submitter_code">
              Submitter Code &nbsp;
            <i className="fa fa-question-circle" data-tooltip-id="tip-submitter-code" data-tooltip-content={submitterCodeTooltip} />
            <Tooltip id="tip-submitter-code" className="registration-tooltip" />
          </label>
          <div className={mainInputClass}>
            <input
              name="insurer[submitter_code]"
              id="insurer_submitter_code"
              value={insurer.submitter_code ?? ""}
              onChange={onInputChange}
              className="form-control"
              placeholder="Submitter Code"
              readOnly={!props.canEditSubmitterCode}
              maxLength={20}
            />
          </div>
        </div>

        {props.config.showSubmitterType && (
          <div className="form-group">
            <label className={props.config.insurerTypeRequired ? labelClassRequired : labelClass} htmlFor="insurer_type">
                Submitter Type
            </label>

            <div className="col-sm-5">
              <select
                name="insurer[type]"
                id="insurer_type"
                value={insurer.type}
                onChange={onInputChange}
                className="form-control"
                required={props.config.insurerTypeRequired}
              >
                <option />
                {insurer.type === config.InsurerType.InsuranceCompanyLegacy && (
                  <option data-show="#naic-number" value={config.InsurerType.InsuranceCompanyLegacy}>
                    {config.InsurerTypeLabels[config.InsurerType.InsuranceCompanyLegacy]}
                  </option>
                )}
                {props.config.submitterTypes.sort().map((submitterType) =>
                  <option
                    key={submitterType}
                    value={submitterType}
                  >
                    {InsurerTypeLabels[submitterType]}
                  </option>
                )}
              </select>
            </div>
            <div className="col-sm-12">
              <hr style={{ margin: "15px 0 5px" }} />
            </div>
          </div>
        )}

        {props.config.showParticipantType && (
          <div className="form-group">
            <label className={labelClassRequired} htmlFor="insurer_participant_type">
              Type of Participant
            </label>

            <div className="col-sm-4">
              <select
                id="insurer_participant_type"
                name="insurer[participant_type]"
                value={insurer.participant_type ?? ""}
                onChange={onInputChange}
                className="form-control"
                required={true}
              >
                <option />
                {config.ParticipantTypes.map((type) => {
                  return (
                    <option key={type} value={type}>{type}</option>
                  )
                })}
              </select>
            </div>

            <div className="col-sm-12">
              <hr style={{ margin: "15px 0 5px" }} />
            </div>
          </div>
        )}

        <NaicFormGroups
          label="NAIC Number"
          naics={naics}
          idPrefix="insurer_naics"
          labelClass={labelClass}
          hr={true}
          onChange={onNaicChange}
          labelColumns={3}
        />

        {props.config.hasFein && (
          <FeinFormGroups
            label="FEIN Number"
            feins={feins}
            required={feinRequired}
            idPrefix="insurer_feins"
            labelColumns={3}
            labelClass={labelClass}
            labelClassRequired={labelClassRequired}
            onChange={onFeinChange}
          />
        )}

        {props.config.licenseTypes && (
          <LicenseTypeMultiSelect
            available_license_types={props.config.licenseTypes}
            selected_licenses_types={insurer.license_types || [{}]}
            max_selected_licenses_types={props.config.maxSelectedLicenseTypes ?? null}
            show_licensed_entity_name={props.config.showLicensedEntityName ?? false}
            required={true}
            onAddLicenseType={addLicenseType}
            onRemoveLicenseType={removeLicenseType}
            onChange={onLicenseTypeChange}
            license_type_field_label={props.config.licenseTypeFieldLabel ?? "License Type"}
            license_type_tooltip={props.config.licenseTypeTooltip}
          />
        )}

        {props.config.marketCategories && (
          <MarketCategoriesMultiSelect
            available_market_categories={props.config.marketCategories}
            selected_market_categories={insurer.market_categories ?? []}
            covered_lives={insurer.market_category_covered_lives ?? []}
            onAdd={addMarketCategory}
            onRemove={removeMarketCategory}
            setFormValue={setFormValue}
          />
        )}

        <div className="form-group">
          <label className={labelClassRequired} htmlFor="insurer_street_1">Address</label>
          <div className={mainInputClass}>
            <input
              name="insurer[street_1]"
              id="insurer_street_1"
              value={insurer.street_1 ?? ""}
              onChange={onInputChange}
              className="form-control"
              placeholder="Mailing Address"
              required={true}
              maxLength={80}
            />
          </div>
        </div>
        <div className="form-group">
          <label className="sr-only" htmlFor="insurer_street_2">Mailing Address (Line 2 - Optional)</label>
          <div className={`${mainInputClass} col-sm-offset-3`}>
            <input
              id="insurer_street_2"
              name="insurer[street_2]"
              value={insurer.street_2 ?? ""}
              onChange={onInputChange}
              className="form-control"
              placeholder="Mailing Address (Line 2 - Optional)"
              aria-label="Mailing Address (Line 2 - Optional)"
              maxLength={80}
            />
          </div>
        </div>
        <div className="form-group">
          <div className={`${mainInputClass} col-sm-offset-3`}>
            <div className="row">
              {shouldDisplayFullAddress && (
                <div className="col-sm-4">
                  <label className="sr-only" htmlFor="insurer_city">City</label>
                  <input
                    name="insurer[city]"
                    id="insurer_city"
                    value={insurer.city ?? ""}
                    onChange={onInputChange}
                    className="form-control city"
                    placeholder="City"
                    aria-label="City"
                    required={true}
                    maxLength={80}
                  />
                </div>
              )}
              {shouldDisplayFullAddress && (
                <div className="col-sm-2">
                  <label className="sr-only" htmlFor="insurer_state">State</label>
                  <input
                    name="insurer[state]"
                    id="insurer_state"
                    value={insurer.state ?? ""}
                    onChange={onInputChange}
                    className="form-control state"
                    placeholder="State"
                    aria-label="State"
                    required={true}
                    maxLength={2}
                  />
                </div>
              )}
              <div className="col-sm-4">
                <label className={insurerZipNotFound ? "has-error" : "required"} htmlFor="insurer_zip">
                  <input
                    name="insurer[zip]"
                    id="insurer_zip"
                    value={insurer.zip ?? ""}
                    onChange={onInputChange}
                    onBlur={onInsurerZipCodeBlur}
                    className="form-control zip"
                    placeholder="ZIP Code"
                    data-fv-zipcode="true"
                    data-fv-zipcode-country="US"
                    data-fv-notempty-message="Please enter a valid ZIP code."
                    data-fv-zipcode-message="Please enter a valid ZIP code."
                    required={true}
                    maxLength={20}
                    style={{ marginBottom: "3px", fontWeight: "normal" }}
                  />
                  {insurerZipNotFound ? <span className="help-block">ZIP Code Not Found. *</span> : "ZIP Code"}
                </label>
              </div>
              {shouldDisplayFullAddress && (
                <div className="col-sm-4">
                  <label className="sr-only" htmlFor="insurer_country_code">Country</label>
                  <select
                    name="insurer[country_code]"
                    id="insurer_country_code"
                    value={insurer.country_code ?? ""}
                    onChange={onInputChange}
                    className="form-control country"
                    aria-label="Country"
                    required={true}
                  >
                    <option value="">Country</option>
                    <option value="US">United States</option>
                    <option value="CA">Canada</option>
                  </select>
                </div>
              )}
            </div>
          </div>
        </div>

        {props.config.showTpaSelfInsuredForm && props.form?.insurer?.type === config.InsurerType.ThirdPartyAdministrator && (
          <TpaSelfInsuredForm form={props.form} dispatch={props.dispatch} />
        )}
      </div>
    </div>
  )
}
